import React, { useState } from "react";
import { Link, useParams, useNavigate } from "react-router-dom";
import { getBookBySlug, books } from "../data/books";
import { getAuthorBySlug } from "../data/authors";
import { useCart } from "../context/CartContext";
import { BookCard } from "../components/BookCard";
import { SEO } from "../components/SEO";
import { InstitutionalModal, defaultEnquiryTypeForBook, ENQUIRY_TYPES } from "../components/InstitutionalModal";
import { toast, Toaster } from "sonner";
import {
  ShoppingBag,
  Star,
  ChevronRight,
  BookOpen,
  Headphones,
  Film,
  GraduationCap,
  Radio,
  Globe,
  Tv,
  Sparkles,
  Package,
  Ticket,
  BookMarked,
  ArrowLeft,
} from "lucide-react";

const ipIcons = {
  Book: BookOpen,
  Ebook: BookMarked,
  Audiobook: Headphones,
  Comic: Sparkles,
  Course: GraduationCap,
  Film: Film,
  Series: Tv,
  Translation: Globe,
  Merchandise: Package,
  Podcast: Radio,
  "Live Event": Ticket,
  "Study Guide": BookMarked,
};

const ipStatusStyle = {
  available: "bg-crimson text-ivory border-crimson",
  "in-production": "bg-ivory text-gold border-gold",
  "not-licensed": "bg-ivory text-ink-muted/50 border-ink-muted/20",
};

const ipStatusLabel = {
  available: "Available",
  "in-production": "In Production",
  "not-licensed": "Not Yet Licensed",
};

export default function BookDetail() {
  const { slug } = useParams();
  const book = getBookBySlug(slug);
  const navigate = useNavigate();
  const { addItem } = useCart();
  const [qty, setQty] = useState(1);
  const [instOpen, setInstOpen] = useState(false);

  if (!book) {
    return (
      <div className="py-40 text-center">
        <p className="font-editorial italic text-3xl text-ink-muted">This book is not yet in our catalogue.</p>
        <Link to="/books" className="mt-8 inline-block text-crimson link-underline uppercase tracking-[0.24em] text-sm">
          ← Back to catalogue
        </Link>
      </div>
    );
  }

  const primaryAuthor = getAuthorBySlug(book.authors[0]);
  const relatedBooks = books.filter((b) => b.slug !== book.slug && (b.imprint === book.imprint || b.authors.some((a) => book.authors.includes(a)))).slice(0, 4);

  // SEO structured data — BreadcrumbList + Book with AggregateRating + Reviews
  const ratings = book.reviews?.map((r) => r.stars) || [];
  const avgRating = ratings.length ? ratings.reduce((a, b) => a + b, 0) / ratings.length : null;
  const bookJsonLd = {
    "@context": "https://schema.org",
    "@graph": [
      {
        "@type": "BreadcrumbList",
        itemListElement: [
          { "@type": "ListItem", position: 1, name: "Home", item: "https://conlangbookhouse.com/" },
          { "@type": "ListItem", position: 2, name: "Books", item: "https://conlangbookhouse.com/books" },
          { "@type": "ListItem", position: 3, name: book.title, item: `https://conlangbookhouse.com/books/${book.slug}` },
        ],
      },
      {
        "@type": "Book",
        name: book.title,
        alternateName: book.subtitle,
        author: primaryAuthor
          ? { "@type": "Person", name: primaryAuthor.name }
          : { "@type": "Organization", name: "Conlang Book House Editorial Board" },
        isbn: book.isbn,
        bookFormat: `https://schema.org/${book.format?.toLowerCase().includes("hardcover") ? "Hardcover" : "Paperback"}`,
        numberOfPages: book.pages,
        inLanguage: book.language,
        publisher: { "@type": "Organization", name: "Conlang Book House", url: "https://conlangbookhouse.com" },
        image: book.cover,
        description: book.editorialNote,
        datePublished: `${book.releaseYear}`,
        url: `https://conlangbookhouse.com/books/${book.slug}`,
        offers: {
          "@type": "Offer",
          price: book.price,
          priceCurrency: book.priceCurrency,
          availability: "https://schema.org/InStock",
          url: `https://conlangbookhouse.com/books/${book.slug}`,
        },
        ...(avgRating && {
          aggregateRating: {
            "@type": "AggregateRating",
            ratingValue: avgRating.toFixed(1),
            bestRating: "5",
            worstRating: "1",
            ratingCount: ratings.length,
            reviewCount: ratings.length,
          },
          review: book.reviews.map((r) => ({
            "@type": "Review",
            reviewRating: { "@type": "Rating", ratingValue: r.stars, bestRating: "5" },
            author: { "@type": "Organization", name: "Anonymous Critic" },
            reviewBody: r.quote,
          })),
        }),
      },
    ],
  };

  const bookMeta = [
    { attr: "property", key: "book:isbn", content: book.isbn },
    { attr: "property", key: "book:release_date", content: `${book.releaseYear}` },
    { attr: "property", key: "product:price:amount", content: `${book.price}` },
    { attr: "property", key: "product:price:currency", content: book.priceCurrency },
    { attr: "property", key: "product:availability", content: "in stock" },
  ];

  const handleAddToCart = () => {
    addItem(book, qty);
    toast.success(`${book.title} added to cart`, { description: `Quantity: ${qty}` });
  };

  const handleBuyNow = () => {
    addItem(book, qty);
    navigate("/checkout");
  };

  return (
    <div data-testid="book-detail-page" style={{ backgroundColor: book.palette.bg }}>
      <SEO
        title={`${book.title} — ${book.subtitle}`}
        path={`/books/${book.slug}`}
        description={book.editorialNote}
        image={book.cover}
        imageAlt={book.coverAlt}
        type="book"
        keywords={`${book.title}, ${book.authors.join(", ")}, ${book.genres.join(", ")}, Conlang Book House, buy ${book.title} online, ${book.isbn}`}
        jsonLd={bookJsonLd}
        extraMeta={bookMeta}
      />
      <Toaster position="top-center" richColors />
      {/* Breadcrumb */}
      <div className="mx-auto max-w-[1400px] px-6 lg:px-10 pt-8">
        <div className="flex items-center gap-2 text-xs uppercase tracking-[0.22em] text-ink-muted">
          <Link to="/" className="hover:text-crimson">Home</Link>
          <ChevronRight className="w-3 h-3" />
          <Link to="/books" className="hover:text-crimson">Books</Link>
          <ChevronRight className="w-3 h-3" />
          <span style={{ color: book.palette.accent }}>{book.title}</span>
        </div>
      </div>

      {/* Hero */}
      <section className="mx-auto max-w-[1400px] px-6 lg:px-10 pt-10 lg:pt-16 pb-20 grid grid-cols-12 gap-8 lg:gap-16">
        {/* Cover */}
        <div className="col-span-12 lg:col-span-5">
          <div className="lg:sticky lg:top-32">
            <img
              src={book.cover}
              alt={book.coverAlt}
              className="w-full max-w-[420px] mx-auto book-cover-3d"
              data-testid="book-cover-image"
            />
          </div>
        </div>

        {/* Detail */}
        <div className="col-span-12 lg:col-span-7">
          <p className="text-[11px] uppercase tracking-[0.32em]" style={{ color: book.palette.accent }} data-testid="book-imprint">
            {book.imprint}
          </p>
          <h1 className="font-editorial text-5xl md:text-6xl lg:text-7xl leading-[0.92] tracking-tight mt-6 text-balance" style={{ color: book.palette.ink }} data-testid="book-title">
            {book.title}
          </h1>
          <p className="editorial-italic text-2xl mt-4" style={{ color: book.palette.ink, opacity: 0.75 }} data-testid="book-subtitle">
            {book.subtitle}
          </p>
          <p className="mt-8 text-lg leading-relaxed max-w-2xl" style={{ color: book.palette.ink, opacity: 0.85 }}>
            {book.tagline}
          </p>

          {/* Meta grid */}
          <div className="mt-10 grid grid-cols-2 md:grid-cols-4 gap-6 pb-8 border-b" style={{ borderColor: `${book.palette.accent}30` }}>
            {[
              ["Format", book.format],
              ["Pages", book.pages],
              ["Language", book.language],
              ["Release", book.releaseYear],
            ].map(([k, v]) => (
              <div key={k}>
                <p className="text-[10px] uppercase tracking-[0.24em] opacity-60" style={{ color: book.palette.ink }}>{k}</p>
                <p className="font-editorial text-xl mt-2" style={{ color: book.palette.ink }}>{v}</p>
              </div>
            ))}
          </div>

          {/* Buy box */}
          <div className="mt-10" data-testid="book-buy-box">
            <div className="flex items-baseline gap-4 mb-6">
              <span className="font-editorial text-5xl" style={{ color: book.palette.ink }} data-testid="book-price">
                ₹{book.price}
              </span>
              <span className="text-sm uppercase tracking-[0.22em] opacity-60" style={{ color: book.palette.ink }}>
                Inc. of all taxes
              </span>
            </div>

            <div className="flex items-center gap-4 mb-6">
              <label className="text-xs uppercase tracking-[0.22em]" style={{ color: book.palette.ink }}>Qty</label>
              <div className="inline-flex border" style={{ borderColor: `${book.palette.accent}60` }}>
                <button
                  onClick={() => setQty((q) => Math.max(1, q - 1))}
                  className="px-4 py-2 hover:bg-black/5"
                  data-testid="book-qty-decrement"
                  aria-label="Decrease"
                  style={{ color: book.palette.ink }}
                >
                  −
                </button>
                <span className="px-6 py-2 min-w-[3rem] text-center border-x" style={{ borderColor: `${book.palette.accent}60`, color: book.palette.ink }} data-testid="book-qty-value">
                  {qty}
                </span>
                <button
                  onClick={() => setQty((q) => q + 1)}
                  className="px-4 py-2 hover:bg-black/5"
                  data-testid="book-qty-increment"
                  aria-label="Increase"
                  style={{ color: book.palette.ink }}
                >
                  +
                </button>
              </div>
            </div>

            <div className="flex flex-col sm:flex-row gap-3">
              <button
                onClick={handleBuyNow}
                data-testid="book-buy-cbh-btn"
                className="flex-1 inline-flex items-center justify-center gap-3 bg-crimson text-ivory px-8 py-5 text-sm uppercase tracking-[0.22em] hover:bg-crimson-dark transition-colors duration-500"
              >
                <ShoppingBag strokeWidth={1.5} className="w-4 h-4" />
                Buy Direct from CBH
              </button>
              <a
                href={book.amazonUrl}
                target="_blank"
                rel="noopener noreferrer"
                data-testid="book-buy-amazon-btn"
                className="flex-1 inline-flex items-center justify-center gap-3 bg-ink text-ivory px-8 py-5 text-sm uppercase tracking-[0.22em] hover:bg-black transition-colors duration-500"
              >
                <span className="text-gold-light font-editorial">a</span>
                Buy on Amazon
              </a>
            </div>
            <button
              onClick={handleAddToCart}
              data-testid="book-add-to-cart-btn"
              className="mt-3 w-full sm:w-auto inline-flex items-center justify-center gap-2 border px-6 py-3 text-xs uppercase tracking-[0.24em] hover:bg-black/5 transition-colors duration-300"
              style={{ borderColor: `${book.palette.accent}60`, color: book.palette.ink }}
            >
              + Add to Cart
            </button>

            <p className="mt-6 text-xs uppercase tracking-[0.22em] opacity-60" style={{ color: book.palette.ink }}>
              ISBN: {book.isbn} · Free shipping on orders above ₹999
            </p>
            <button
              onClick={() => setInstOpen(true)}
              data-testid="book-bulk-cta"
              className="mt-4 w-full inline-flex items-center justify-center gap-2 text-[11px] uppercase tracking-[0.28em] border py-3 hover:bg-black/5 transition-colors duration-300"
              style={{ borderColor: `${book.palette.accent}60`, color: book.palette.ink }}
            >
              Bulk & Institutional Orders · {ENQUIRY_TYPES[defaultEnquiryTypeForBook(book)].label} →
            </button>
          </div>

          {/* Editorial note */}
          <div className="mt-12 border-l-2 pl-6 py-2" style={{ borderColor: book.palette.accent }} data-testid="book-editorial-note">
            <p className="text-[10px] uppercase tracking-[0.32em] mb-3" style={{ color: book.palette.accent }}>Editorial Note</p>
            <p className="editorial-italic text-lg leading-relaxed" style={{ color: book.palette.ink }}>
              {book.editorialNote}
            </p>
          </div>
        </div>
      </section>

      {/* Synopsis */}
      <section className="bg-ivory py-24" data-testid="book-synopsis-section">
        <div className="mx-auto max-w-4xl px-6">
          <p className="text-[11px] uppercase tracking-[0.32em] text-crimson mb-6">§ Synopsis</p>
          <h2 className="font-editorial text-4xl md:text-5xl text-ink leading-tight mb-10 text-balance">
            Inside the book.
          </h2>
          <div className="text-lg leading-relaxed text-ink-soft space-y-6 dropcap">
            {book.synopsis.split("\n\n").map((para, i) => (
              <p key={i}>{para}</p>
            ))}
          </div>
        </div>
      </section>

      {/* Table of Contents */}
      <section className="bg-ivory-soft py-24" data-testid="book-toc-section">
        <div className="mx-auto max-w-4xl px-6">
          <p className="text-[11px] uppercase tracking-[0.32em] text-crimson mb-6">§ Table of Contents</p>
          <h2 className="font-editorial text-4xl md:text-5xl text-ink leading-tight mb-10">Chapters.</h2>
          <ul className="divide-y divide-crimson/15 border-y border-crimson/15">
            {book.tableOfContents.map((ch, i) => (
              <li key={i} className="flex items-baseline gap-6 py-4 hover:bg-crimson/5 transition-colors duration-300 px-2">
                <span className="editorial-italic text-crimson w-10 text-right">{String(i + 1).padStart(2, "0")}</span>
                <span className="font-editorial text-xl text-ink">{ch}</span>
              </li>
            ))}
          </ul>
        </div>
      </section>

      {/* IP Surface */}
      <section className="bg-ivory py-24" data-testid="book-ip-section">
        <div className="mx-auto max-w-[1200px] px-6">
          <p className="text-[11px] uppercase tracking-[0.32em] text-crimson mb-6">§ IP Surface</p>
          <h2 className="font-editorial text-4xl md:text-5xl text-ink leading-tight mb-4">More than a book.</h2>
          <p className="text-ink-muted mb-12 max-w-2xl">
            Every CBH title is tracked across twelve revenue surfaces. This grid shows what&rsquo;s already out,
            what&rsquo;s in production, and what&rsquo;s still available for licensing.
          </p>
          <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-6 gap-3">
            {Object.entries(book.ipSurface).map(([surface, status]) => {
              const Icon = ipIcons[surface] || Sparkles;
              return (
                <div
                  key={surface}
                  data-testid={`ip-surface-${surface.toLowerCase().replace(/\s+/g, "-")}`}
                  className={`border p-4 flex flex-col items-start gap-3 transition-colors duration-500 ${ipStatusStyle[status]}`}
                >
                  <Icon strokeWidth={1.25} className="w-6 h-6" />
                  <div>
                    <p className="text-sm font-semibold">{surface}</p>
                    <p className="text-[10px] uppercase tracking-[0.22em] opacity-80">{ipStatusLabel[status]}</p>
                  </div>
                </div>
              );
            })}
          </div>
          <div className="mt-8">
            <Link to="/contact?inquiry_type=rights" className="text-sm uppercase tracking-[0.22em] text-crimson link-underline">
              Enquire about rights & licensing →
            </Link>
          </div>
        </div>
      </section>

      {/* Author */}
      {primaryAuthor && (
        <section className="bg-ivory-soft py-24 grain-overlay" data-testid="book-author-section">
          <div className="mx-auto max-w-[1200px] px-6 grid grid-cols-12 gap-10">
            <div className="col-span-12 md:col-span-4">
              <div
                className="aspect-square flex items-center justify-center font-editorial text-8xl text-ivory"
                style={{ backgroundColor: primaryAuthor.accent }}
              >
                {primaryAuthor.photoInitials}
              </div>
            </div>
            <div className="col-span-12 md:col-span-8">
              <p className="text-[11px] uppercase tracking-[0.32em] text-crimson mb-4">§ About the Author</p>
              <h2 className="font-editorial text-4xl md:text-5xl text-ink leading-tight mb-2" data-testid="book-author-name">
                {primaryAuthor.name}
              </h2>
              <p className="text-[11px] uppercase tracking-[0.24em] text-gold-dark mb-6">{primaryAuthor.tier}</p>
              <p className="text-lg text-ink-soft leading-relaxed mb-6">{primaryAuthor.shortBio}</p>
              <Link
                to={`/authors/${primaryAuthor.slug}`}
                data-testid="book-author-link"
                className="inline-flex items-center gap-2 text-sm uppercase tracking-[0.22em] text-crimson link-underline"
              >
                Read full profile →
              </Link>
            </div>
          </div>
        </section>
      )}

      {/* Reviews */}
      <section className="bg-ivory py-24" data-testid="book-reviews-section">
        <div className="mx-auto max-w-[1200px] px-6">
          <p className="text-[11px] uppercase tracking-[0.32em] text-crimson mb-6">§ Press & Reviews</p>
          <h2 className="font-editorial text-4xl md:text-5xl text-ink leading-tight mb-12">The critics say.</h2>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-10">
            {book.reviews.map((r, i) => (
              <div key={i} className="border-l-2 border-crimson pl-8 py-4">
                <div className="flex gap-1 mb-4" data-testid={`book-review-stars-${i}`}>
                  {Array.from({ length: r.stars }).map((_, s) => (
                    <Star key={s} strokeWidth={1.25} fill="#B08B3A" className="w-4 h-4 text-gold" />
                  ))}
                </div>
                <p className="editorial-italic text-2xl leading-tight text-ink text-balance" data-testid={`book-review-quote-${i}`}>
                  &ldquo;{r.quote}&rdquo;
                </p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Related */}
      {relatedBooks.length > 0 && (
        <section className="bg-ivory-soft py-24" data-testid="book-related-section">
          <div className="mx-auto max-w-[1400px] px-6 lg:px-10">
            <div className="flex items-end justify-between mb-12 flex-wrap gap-4">
              <div>
                <p className="text-[11px] uppercase tracking-[0.32em] text-crimson mb-4">§ From the same shelf</p>
                <h2 className="font-editorial text-4xl md:text-5xl text-ink leading-tight">You may also read.</h2>
              </div>
              <Link to="/books" className="text-sm uppercase tracking-[0.22em] text-ink hover:text-crimson link-underline">
                Full catalogue →
              </Link>
            </div>
            <div className="grid grid-cols-2 lg:grid-cols-4 gap-10">
              {relatedBooks.map((b, i) => (
                <BookCard key={b.slug} book={b} index={i} />
              ))}
            </div>
          </div>
        </section>
      )}

      {/* Back link */}
      <div className="bg-ivory py-12">
        <div className="mx-auto max-w-[1400px] px-6 lg:px-10">
          <Link to="/books" className="inline-flex items-center gap-2 text-sm uppercase tracking-[0.24em] text-crimson link-underline">
            <ArrowLeft className="w-4 h-4" /> Back to catalogue
          </Link>
        </div>
      </div>

      {/* Institutional / Bulk Orders modal */}
      <InstitutionalModal
        open={instOpen}
        onClose={() => setInstOpen(false)}
        defaultType={defaultEnquiryTypeForBook(book)}
        book={book}
      />
    </div>
  );
}
